<template>
  <div class="space-y-8">
    <!-- Company Users Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>

            <div>
              <h3 class="text-lg font-semibold text-gray-900">
                {{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.header.users') }}
              </h3>

              <p class="text-sm text-gray-600">
                Manage users with access to this company's projects
              </p>
            </div>
          </div>

          <div class="flex items-center space-x-3">
            <div class="text-right">
              <p class="text-sm text-gray-500">
                {{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.header.totalUsers') }}
              </p>

              <p class="text-lg font-semibold text-gray-900">
                {{ users.length }}
              </p>
            </div>

            <div class="flex items-center space-x-2">
              <button
                type="button"
                class="btn--secondary-blue btn flex items-center space-x-2"
                @click="showRolesModal = true"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4">
    <path d="M9 12L11 14L15 9.99999M20 12C20 16.4611 14.54 19.6937 12.6414 20.683C12.4361 20.79 12.3334 20.8435 12.191 20.8712C12.08 20.8928 11.92 20.8928 11.809 20.8712C11.6666 20.8435 11.5639 20.79 11.3586 20.683C9.45996 19.6937 4 16.4611 4 12V8.21759C4 7.41808 4 7.01833 4.13076 6.6747C4.24627 6.37113 4.43398 6.10027 4.67766 5.88552C4.9535 5.64243 5.3278 5.50207 6.0764 5.22134L11.4382 3.21067C11.6461 3.13271 11.75 3.09373 11.857 3.07827C11.9518 3.06457 12.0482 3.06457 12.143 3.07827C12.25 3.09373 12.3539 3.13271 12.5618 3.21067L17.9236 5.22134C18.6722 5.50207 19.0465 5.64243 19.3223 5.88552C19.566 6.10027 19.7537 6.37113 19.8692 6.6747C20 7.01833 20 7.41808 20 8.21759V12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

                <span>{{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.buttons.viewRoles') }}</span>
              </button>

              <button
                type="button"
                class="btn btn--primary-blue flex items-center space-x-2"
                @click="showInviteModal = true"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                <span>{{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.buttons.inviteUser') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Loading State -->
        <div
          v-if="fetchCompanyUsersAction.is(States.LOADING)"
          class="text-center py-12"
        >
          <AppSpinner class="h-8 w-8 mx-auto mb-4" />

          <p class="text-gray-600">
            Loading users...
          </p>
        </div>

        <!-- Users List -->
        <div
          v-else-if="fetchCompanyUsersAction.is(States.COMPLETE) && users.length > 0"
          class="space-y-4"
        >
          <CompanyUserRow
            v-for="user in users"
            :key="user.id"
            :user="user"
            :company="company"
          />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="fetchCompanyUsersAction.is(States.COMPLETE) && users.length === 0"
          class="text-center py-12"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mx-auto h-12 w-12 text-gray-400 mb-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.messages.noUsersFound') }}
          </h3>

          <p class="text-sm text-gray-500 mb-6">
            Get started by inviting your first user to the company.
          </p>

          <button
            type="button"
            class="btn btn--primary-blue flex items-center space-x-2 mx-auto"
            @click="showInviteModal = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <span>{{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.buttons.inviteUser') }}</span>
          </button>
        </div>
      </div>
    </div>

    <InviteUserModal
      v-if="showInviteModal"
      :company-id="$props.companyId"
      @close="showInviteModal = false"
    />

    <RolesPermissionsModal
      v-if="showRolesModal"
      @close="showRolesModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import Permissions from '@/models/Permissions';
import { useCompaniesStore } from '@/store/companies';
import { useProjectsStore } from '@/store/projects';
import { useRolesStore } from '@/store/roles';
import { useUserStore } from '@/store/user';
import { useDebounceFn } from '@vueuse/core';
import { useRepo } from 'pinia-orm';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import CompanyUserRow from './CompanyUserRow.vue';
import InviteUserModal from './InviteUserModal.vue';
import RolesPermissionsModal from './RolesPermissionsModal.vue';

const props = defineProps<{
  companyId: number;
}>();

const emits = defineEmits<{
  (e: 'dirty');
  (e: 'saved');
}>();

const companiesStore = useCompaniesStore();
const userStore = useUserStore();
const projectsStore = useProjectsStore();
const fetchCompanyUsersAction = useStoreApiAction(userStore.fetchCompanyUsersIndex);

fetchCompanyUsersAction.request(props.companyId).catch((error) => {
  Swal.fire({
    icon: 'error',
    title: t('ProjectSettings.CompanyTab.CompanyUsersTab.messages.fetchUsersFailed'),
  });
});

const users = computed(() => {
  return userStore.models.query().whereHas('projects', (query) => {
    query.where('company_id', props.companyId);
  })
    .whereHas('roles', (query) => {
      query
        .whereIn('name', [
          'company_admin',
          'project_admin',
          'engineer',
          'site_manager',
          'contract_admin',
          'contract_manager',
        ]);
    })
    .with('projects', (query) => {
      query.where('company_id', props.companyId);
    }).whereNotIn('id', [userStore.user.id]).get();
});

const company = computed(() => {
  return companiesStore.models.query().with('projects').find(props.companyId);
});

const rolesStore = useRolesStore();

rolesStore.fetchRoles();

const showInviteModal = ref(false);
const showRolesModal = ref(false);
</script>

<style scoped></style>
